import {
  COLLECTION_TYPENAMES,
  getLinkFromProductVariant,
  getProductTypeFromTypename,
  getProductTypeFromVariant,
} from 'bundles/collections-carousel/constants';
import type { CarouselCollection } from 'bundles/collections-carousel/types/CarouselCollection';
import type {
  CatalogCollectionPageQuery_CatalogCollectionPagesV1Resource_bySlug_elements as CatalogCollectionPage,
  CatalogCollectionPageQuery_CatalogCollectionPagesV1Resource_bySlug_elements_courses_elements as CoursesV1,
  CatalogCollectionPageQuery_CatalogCollectionPagesV1Resource_bySlug_elements_s12ns_elements as S12n,
} from 'bundles/coursera-plus-landing-page/queries/__generated__/CatalogCollectionPageQuery';
import { getTranslatedDifficulty } from 'bundles/search-common/constants';

export type BaseCoursesV1 = Pick<CoursesV1, 'id' | '__typename' | 'level' | 'slug' | 'name'>;

export type BaseOnDemandSpecializationsV1 = Pick<S12n, 'id' | '__typename' | 'metadata' | 'slug' | 'name'>;

type CollectionEntry = (CoursesV1 | S12n) & { logo: string | null; productVariant: string | null };

const removeNull = <K>(x: K | null): x is K => x !== null;

const formatCatalogCollection = (catalogCollectionPage: CatalogCollectionPage) => {
  if (!catalogCollectionPage) return catalogCollectionPage;
  const { collection, courses, s12ns, ...rest } = catalogCollectionPage;
  const allCollectionItems = [...(courses?.elements || []), ...(s12ns?.elements || [])].filter(removeNull);
  const collectionEntries = collection.entries.map((entry) => {
    const matchedCollectionItem = allCollectionItems.find((c) => c?.id === entry.id);

    if (!matchedCollectionItem) {
      return null;
    }

    return {
      ...entry,
      ...matchedCollectionItem,
    };
  });
  return { entries: collectionEntries?.filter(removeNull) as CollectionEntry[], ...rest };
};

const getProductDifficultyLevel = (entry: CollectionEntry) => {
  if (entry.__typename === 'CoursesV1') {
    return entry.level && getTranslatedDifficulty(entry.level);
  } else {
    return entry.metadata.level && getTranslatedDifficulty(entry.metadata.level);
  }
};

export const mapCatalogCollectionToCarouselCollection = (
  catalogCollectionPage: CatalogCollectionPage
): CarouselCollection => {
  const collection = formatCatalogCollection(catalogCollectionPage);
  const carouselEntities = collection.entries.map((entry, index) => {
    const productType = getProductTypeFromTypename(entry.__typename) || getProductTypeFromVariant(entry.productVariant);
    return {
      id: entry.id,
      name: entry.name,
      slug: entry.slug,
      forceOpenInNewTab: true,
      imageUrl: entry.__typename === COLLECTION_TYPENAMES.COURSES ? entry.photoUrl : entry.logo,
      link: getLinkFromProductVariant(entry.slug, entry.__typename, entry.productVariant),
      partners:
        entry.partners &&
        entry.partners.elements.map((partner) => {
          return {
            id: partner?.id,
            logo: partner?.squareLogo || partner?.logo,
            name: partner?.name,
          };
        }),
      productDifficultyLevel: getProductDifficultyLevel(entry),
      partnerLogos:
        entry.partners &&
        entry.partners.elements.map((partner) => partner?.squareLogo || partner?.logo).filter(Boolean),
      productType,
      trackingData: {
        entityId: `${productType}~${entry.id}`,
        entityIndex: index,
        name: entry.name,
        slug: entry.slug,
        offeringType: productType,
        id: entry.id,
      },
    };
  });

  return {
    collectionId: collection.id,
    title: '',
    entities: carouselEntities,
    collectionTrackingData: {
      label: collection.title,
      collectionId: collection.id,
      collectionIndex: 0,
      collectionName: collection.title,
    },
  } as CarouselCollection;
};
